import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, TrendingUp, MessageCircle } from 'lucide-react';
import { Reveal } from '../components/Reveal';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FloatingWhatsApp } from '../components/WhatsAppButton';
import { useLang } from '../i18n';
import { CASOS_DETALLE } from '../data/casosDetalle';

const API = process.env.REACT_APP_BACKEND_URL;

const WHATSAPP = 'https://wa.me/34667312553?text=' + encodeURIComponent('Hola #PICHONTEAM, he visto vuestro caso de éxito y quiero hablar con vosotros');

function PlaceholderMarca() {
    return (
        <div className="rounded-[1.6rem] border border-dashed border-ink/25 bg-white/60 p-10 text-center" data-testid="caso-pendiente">
            <p className="font-display text-xl font-semibold text-ink/70">Fotos y contenido de marca en preparación</p>
            <p className="text-sm text-ink/50 mt-2 max-w-md mx-auto">En cuanto el cliente pase las imágenes y los textos de este caso, se publicarán aquí siguiendo el modelo de El Gato con Copas.</p>
        </div>
    );
}

function GaleriaProceso({ caso, t }) {
    const [active, setActive] = useState(0);
    const escena = caso.proceso[active];
    const prev = () => setActive((a) => (a - 1 + caso.proceso.length) % caso.proceso.length);
    const next = () => setActive((a) => (a + 1) % caso.proceso.length);
    return (
        <div data-testid="caso-proceso">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 overflow-x-auto pb-2" data-testid="caso-proceso-tabs">
                {caso.proceso.map((p, i) => (
                    <button
                        key={p.titulo}
                        onClick={() => setActive(i)}
                        className={`shrink-0 rounded-full pl-3 pr-5 py-2 border transition-all duration-300 ${active === i ? 'bg-ink text-pastelcream border-ink' : 'bg-white border-ink/15 text-ink/60 hover:border-crimson hover:text-crimson'}`}
                        data-testid={`caso-proceso-tab-${i + 1}`}
                    >
                        <span className="text-[11px] sm:text-[12px] font-bold tracking-[0.1em] uppercase whitespace-nowrap">{String(i + 1).padStart(2, '0')} · {p.titulo}</span>
                    </button>
                ))}
            </div>

            <div className="relative min-h-[420px] sm:min-h-[480px] mt-8">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={active}
                        initial={{ opacity: 0, y: 26 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -18 }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center"
                        data-testid={`caso-proceso-panel-${active + 1}`}
                    >
                        <div className="lg:col-span-6">
                            <div className="rounded-[1.6rem] overflow-hidden border border-ink/15 shadow-[0_30px_60px_-30px_rgba(27,42,74,0.4)] bg-white">
                                <img src={escena.img} alt={escena.titulo} className="w-full h-[340px] sm:h-[460px] object-contain" data-testid="caso-proceso-image" />
                            </div>
                        </div>
                        <div className="lg:col-span-6">
                            <p className="font-ink text-[11px] uppercase tracking-[0.3em] text-crimson" data-testid="caso-proceso-number">
                                {String(active + 1).padStart(2, '0')} / {String(caso.proceso.length).padStart(2, '0')}
                            </p>
                            <h3 className="font-display text-2xl sm:text-3xl font-semibold text-ink mt-4">{escena.titulo}</h3>
                            <p className="mt-5 text-base sm:text-lg leading-relaxed text-ink/75" data-testid="caso-proceso-text">{escena.texto}</p>
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>

            <div className="flex items-center justify-between border-t border-ink/10 pt-6 mt-6">
                <span className="font-ink text-[10px] uppercase tracking-[0.3em] text-ink/40">
                    {String(active + 1).padStart(2, '0')} / {String(caso.proceso.length).padStart(2, '0')}
                </span>
                <div className="flex gap-3">
                    <button onClick={prev} aria-label="Anterior" className="w-11 h-11 rounded-full border border-ink/15 flex items-center justify-center text-ink hover:border-crimson hover:text-crimson transition-colors" data-testid="caso-proceso-prev">
                        <ArrowLeft size={17} />
                    </button>
                    <button onClick={next} aria-label="Siguiente" className="w-11 h-11 rounded-full bg-ink text-pastelcream flex items-center justify-center hover:bg-crimson transition-colors" data-testid="caso-proceso-next">
                        <ArrowRight size={17} />
                    </button>
                </div>
            </div>
        </div>
    );
}

function MarcaBlocks({ caso, t }) {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="caso-marca-blocks">
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="rounded-[1.4rem] bg-white border border-ink/10 p-7" data-testid="caso-antes">
                    <p className="font-ink text-[10px] uppercase tracking-[0.24em] text-ink/45">{t('caso_antes')}</p>
                    <div className="mt-5 rounded-2xl bg-canvas border border-ink/10 overflow-hidden">
                        <img src={caso.antesImg} alt={t('caso_antes')} className="w-full h-56 object-contain" />
                    </div>
                </div>
                <div className="rounded-[1.4rem] bg-ink text-pastelcream p-7" data-testid="caso-despues">
                    <p className="font-ink text-[10px] uppercase tracking-[0.24em] text-pastelcream/60">{t('caso_despues')}</p>
                    <div className="mt-5 rounded-2xl bg-white overflow-hidden">
                        <img src={caso.brandingImg} alt={t('caso_despues')} className="w-full h-56 object-contain" />
                    </div>
                </div>
            </div>

            <div className="rounded-[1.4rem] bg-white border border-ink/10 p-7" data-testid="caso-paleta">
                <p className="font-ink text-[10px] uppercase tracking-[0.24em] text-ink/45">{t('caso_paleta')}</p>
                <div className="mt-6 space-y-4">
                    {caso.paleta.map((c) => (
                        <div key={c.hex} className="flex items-center gap-4" data-testid={`caso-color-${c.hex.replace('#', '')}`}>
                            <span className="w-14 h-14 rounded-2xl border border-ink/15 shrink-0" style={{ backgroundColor: c.hex }} />
                            <div>
                                <p className="font-display font-semibold text-ink">{c.nombre}</p>
                                <p className="font-ink text-[11px] text-ink/55 mt-0.5">{c.hex.toUpperCase()} · RGB {c.rgb}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="rounded-[1.4rem] bg-white border border-ink/10 p-7" data-testid="caso-tipografia">
                <p className="font-ink text-[10px] uppercase tracking-[0.24em] text-ink/45">{t('caso_tipografia')}</p>
                <div className="mt-6 rounded-2xl bg-canvas border border-ink/10 p-6">
                    {caso.tipografia.muestras ? (
                        <div className="space-y-5">
                            {caso.tipografia.muestras.map((m) => (
                                <div key={m.clase}>
                                    <p className={`${m.clase} text-4xl text-ink leading-none`} data-testid={`caso-tipografia-muestra-${m.clase}`}>Aa</p>
                                    <p className="font-ink text-[11px] uppercase tracking-[0.2em] text-ink/50 mt-2">{m.fuente}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="font-display text-6xl text-ink leading-none">Aa</p>
                    )}
                    <p className="font-display text-lg font-semibold text-ink mt-4">{caso.tipografia.familia}</p>
                    <p className="text-sm text-ink/60 mt-2 leading-relaxed">{caso.tipografia.nota}</p>
                </div>
            </div>

            <div className="lg:col-span-2 rounded-[1.4rem] bg-pastelcream border border-ink/10 p-8 lg:p-10 relative" data-testid="caso-valor">
                <p className="font-ink text-[10px] uppercase tracking-[0.24em] text-crimson">{t('caso_valor')}</p>
                <blockquote className="font-display text-xl sm:text-2xl leading-relaxed text-ink mt-5">
                    “{caso.propuestaValor}”
                </blockquote>
                <p className="font-ink text-[11px] uppercase tracking-[0.2em] text-ink/50 mt-5">{caso.propuestaValorAutor}</p>
            </div>
        </div>
    );
}

export default function CasoDetalle() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { t } = useLang();
    const caso = CASOS_DETALLE[slug];
    const [db, setDb] = useState(null);

    useEffect(() => {
        if (!caso) return;
        fetch(`${API}/api/cases`)
            .then((r) => (r.ok ? r.json() : []))
            .then((lista) => setDb(lista.find((c) => c.cliente === caso.cliente) || null))
            .catch(() => setDb(null));
    }, [slug]);

    if (!caso) {
        return (
            <div className="min-h-screen bg-canvas pt-40 pb-24 text-center px-6" data-testid="caso-no-encontrado">
                <p className="font-display text-4xl text-ink">Caso no encontrado</p>
                <button onClick={() => navigate('/')} className="mt-8 rounded-full bg-ink text-pastelcream px-6 py-3 text-[12px] font-bold uppercase tracking-[0.14em] hover:bg-crimson transition-colors" data-testid="caso-volver-home">
                    Volver al inicio
                </button>
            </div>
        );
    }

    const otros = Object.entries(CASOS_DETALLE).filter(([s]) => s !== slug);
    const metricas = (db?.metricas) || [];
    const titulo = `${caso.cliente} — ${t('caso_eyebrow')} | #PICHONTEAM Growth Partners`;
    const desc = `${caso.cliente}: ${db?.resultado || caso.propuestaValor || 'caso de éxito de #PICHONTEAM Growth Partners'}`;

    return (
        <div className="grain bg-canvas text-ink min-h-screen" data-testid="caso-detalle">
            <SEO title={titulo} description={desc} path={`/casos/${slug}`} />
            <Header />
            <main>
            <section className="pt-32 lg:pt-40 pb-16 px-4 sm:px-6" data-testid="caso-hero">
                <div className="max-w-6xl mx-auto">
                    <Link to="/" className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-ink/55 hover:text-crimson transition-colors" data-testid="caso-back">
                        <ArrowLeft size={14} /> {t('caso_back')}
                    </Link>
                    <div className="mt-8 flex flex-wrap items-end justify-between gap-6">
                        <div>
                            <p className="font-ink text-[11px] uppercase tracking-[0.3em] text-crimson">{t('caso_eyebrow')} · {caso.sector}</p>
                            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-ink mt-4" data-testid="caso-nombre">{caso.cliente}</h1>
                            <div className="flex flex-wrap gap-2 mt-5">
                                {caso.serviciosChips.map((s) => (
                                    <span key={s} className="rounded-full bg-white border border-ink/15 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-ink/70">{s}</span>
                                ))}
                            </div>
                        </div>
                        {metricas.length > 0 && (
                            <div className="rounded-[1.4rem] bg-ink text-pastelcream px-7 py-6 flex items-center gap-4" data-testid="caso-metrica-hero">
                                <TrendingUp size={26} className="text-pastelpink" />
                                <div>
                                    <p className="font-display text-2xl sm:text-3xl font-semibold">{metricas[0].valor}</p>
                                    <p className="text-[11px] uppercase tracking-[0.14em] text-pastelcream/60 mt-1">{metricas[0].etiqueta}</p>
                                </div>
                            </div>
                        )}
                    </div>
                    {db?.resultado && (
                        <p className="mt-8 max-w-3xl text-lg leading-relaxed text-ink/75" data-testid="caso-resultado-intro">{caso.intro || db.resultado}</p>
                    )}
                </div>
            </section>

            <section className="py-16 lg:py-20 px-4 sm:px-6 border-t border-ink/10">
                <div className="max-w-6xl mx-auto">
                    {caso.pendiente ? (
                        <PlaceholderMarca />
                    ) : (
                        <>
                            <Reveal><h2 className="font-display text-3xl sm:text-4xl font-semibold text-ink" data-testid="caso-proceso-title">{t('caso_proceso')}</h2></Reveal>
                            <div className="mt-12"><GaleriaProceso caso={caso} t={t} /></div>
                            {caso.ejes?.length > 0 && (
                                <div className="mt-20" data-testid="caso-ejes">
                                    <Reveal><h2 className="font-display text-3xl sm:text-4xl font-semibold text-ink">{t('caso_estrategia')}</h2></Reveal>
                                    <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
                                        {caso.ejes.map((eje, i) => (
                                            <Reveal key={eje.titulo} delay={0.06 * i}>
                                                <div className="h-full rounded-[1.4rem] bg-white border border-ink/10 p-7" data-testid={`caso-eje-${i + 1}`}>
                                                    <p className="font-ink text-[11px] uppercase tracking-[0.3em] text-crimson">{String(i + 1).padStart(2, '0')}</p>
                                                    <h3 className="font-display text-xl font-semibold text-ink mt-4 leading-snug">{eje.titulo}</h3>
                                                    <p className="text-[15px] text-ink/70 leading-relaxed mt-4">{eje.texto}</p>
                                                </div>
                                            </Reveal>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {caso.resultadoIntro && (
                                <div className="mt-20" data-testid="caso-resultado">
                                    <Reveal><h2 className="font-display text-3xl sm:text-4xl font-semibold text-ink">{t('caso_resultado')}</h2></Reveal>
                                    <Reveal delay={0.08}>
                                        <p className="mt-8 max-w-4xl text-lg leading-relaxed text-ink/75">{caso.resultadoIntro}</p>
                                    </Reveal>
                                </div>
                            )}
                            <div className="mt-20"><Reveal><MarcaBlocks caso={caso} t={t} /></Reveal></div>
                        </>
                    )}
                </div>
            </section>

            <section className="py-16 lg:py-24 px-4 sm:px-6 bg-ink text-pastelcream" data-testid="caso-cta">
                <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
                    <div>
                        <h2 className="font-display text-3xl sm:text-4xl font-semibold">{t('caso_cta')}</h2>
                        <p className="text-pastelcream/70 mt-3 max-w-xl">{caso.cliente} · {caso.serviciosChips.join(' · ')}</p>
                    </div>
                    <div className="flex flex-wrap gap-4">
                        <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="btn-shine inline-flex items-center gap-2 rounded-full bg-crimson px-7 py-4 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-crimsondark transition-colors" data-testid="caso-whatsapp-cta">
                            <MessageCircle size={16} /> WhatsApp
                        </a>
                        <button onClick={() => navigate('/')} className="rounded-full border border-pastelcream/30 px-7 py-4 text-[12px] font-bold uppercase tracking-[0.12em] hover:border-pastelpink hover:text-pastelpink transition-colors" data-testid="caso-agendar-cta">
                            {t('cta_agendar')}
                        </button>
                    </div>
                </div>
            </section>

            <section className="py-16 px-4 sm:px-6" data-testid="caso-otros">
                <div className="max-w-6xl mx-auto">
                    <h3 className="font-ink text-[11px] uppercase tracking-[0.3em] text-ink/45">{t('caso_back')}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
                        {otros.map(([s, o]) => (
                            <button
                                key={s}
                                onClick={() => navigate(`/casos/${s}`)}
                                className="group text-left rounded-[1.4rem] bg-white border border-ink/10 p-7 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(27,42,74,0.4)] transition-all duration-300"
                                data-testid={`caso-otro-${s}`}
                            >
                                <p className="font-ink text-[10px] uppercase tracking-[0.2em] text-crimson">{o.sector}</p>
                                <p className="font-display text-2xl font-semibold text-ink mt-3">{o.cliente}</p>
                                <p className="text-sm text-ink/60 mt-2">{o.serviciosChips.join(' · ')}</p>
                                <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-ink/50 group-hover:text-crimson transition-colors mt-4">
                                    {t('caso_ver')} <ArrowRight size={13} />
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            </section>
            </main>
            <Footer />
            <FloatingWhatsApp message={`Hola #PICHONTEAM, he visto el caso de ${caso.cliente} y quiero hablar con vosotros`} />
        </div>
    );
}
